import type { ComponentType } from 'react';
import { useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import type { ChartData, ChartOptions } from 'chart.js';

import type {
  DashboardWidgetProps,
  UpstreamApiDashboardData,
} from 'src/types/dashboard';
import type { ChartConfig } from 'src/services/chartDataTransformation';
import { transformChartData } from 'src/services/chartDataTransformation';

import {
  applyDefaultColors,
  applySeriesOverrides,
  createDefaultPalette,
  DEFAULT_CHART_OPTIONS,
  FALLBACK_CHART_OPTIONS,
  mergePlugins,
  normalizeChartConfig,
} from './helper';

type ChartComponentProps = {
  data: ChartData;
  options: ChartOptions;
};

type SupportedChartType = 'bar' | 'line';

type FlexibleChartState = {
  chartConfig: ChartConfig | null;
  chartType: string;
  chartTypeSupported: boolean;
  ChartComponent: ComponentType<ChartComponentProps> | null;
  chartData: ChartData | null;
  chartOptions: ChartOptions;
  error: string | null;
};

type UseFlexibleChartArgs = {
  config: DashboardWidgetProps['config'];
  data: UpstreamApiDashboardData | undefined;
  noData: boolean;
};

const DEFAULT_CHART_TYPE: SupportedChartType = 'bar';

const CHART_COMPONENTS: Record<
  SupportedChartType,
  ComponentType<ChartComponentProps>
> = {
  bar: Bar as ComponentType<ChartComponentProps>,
  line: Line as ComponentType<ChartComponentProps>,
};

const resolveChartRendering = (chartType: string = DEFAULT_CHART_TYPE) => {
  const ChartComponent =
    CHART_COMPONENTS[chartType as SupportedChartType] ?? null;

  return {
    chartType,
    ChartComponent,
    chartTypeSupported: Boolean(ChartComponent),
  };
};

export const useFlexibleChart = ({
  config,
  data,
  noData,
}: UseFlexibleChartArgs): FlexibleChartState => {
  const { i18n, ready: translationReady } = useTranslation('dashboard');
  const { colors } = useTheme();
  const chartConfig = useMemo(() => normalizeChartConfig(config), [config]);

  const selectedResults = useMemo(() => data?.selected_results ?? [], [data]);

  const effectiveChartConfig = useMemo(() => {
    if (!chartConfig || !selectedResults.length) {
      return chartConfig;
    }

    const datalineLabels = {
      ...(chartConfig.labels?.datalineLabels ?? {}),
    } as Record<string, string>;

    // extract orgName labels
    selectedResults.forEach((result, index) => {
      const key = `selected_${index}`;
      const orgUnit = result.org_units?.[0];
      const orgName = orgUnit?.name;
      if (orgName) {
        datalineLabels[key] = orgName;
      }
    });

    return {
      ...chartConfig,
      selections: {
        ...chartConfig.selections,
        datalines: {
          ...chartConfig.selections.datalines,
          // selectedResults override any other dataline selection options
          focal: false,
          children: 'none',
          ancestors: false,
          selected: selectedResults.map((_, index) => index),
        },
      },
      labels: {
        ...chartConfig.labels,
        datalineLabels,
      },
    } satisfies ChartConfig;
  }, [chartConfig, selectedResults]);

  const { chartType, ChartComponent, chartTypeSupported } =
    resolveChartRendering(config.chartconfig?.type);

  const defaultPalette = useMemo(() => createDefaultPalette(colors), [colors]);

  const dataSeriesConfig = config.data_series_config;

  const transformResult = useMemo(() => {
    if (!chartTypeSupported || !data || noData || !effectiveChartConfig)
      return {
        result: null,
        error: null,
      };

    try {
      return {
        result: transformChartData(data, effectiveChartConfig),
        error: null,
      };
    } catch (error) {
      Sentry.captureException(error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error transforming chart data';
      return { result: null, error: errorMessage };
    }
  }, [
    chartTypeSupported,
    data,
    effectiveChartConfig,
    noData,
    i18n.language,
    translationReady,
  ]);

  const { chartData, chartDataError } = useMemo(() => {
    if (
      transformResult.error ||
      !transformResult.result ||
      !effectiveChartConfig
    )
      return { chartData: null, chartDataError: transformResult.error };

    const withOverrides = applySeriesOverrides(
      transformResult.result.data,
      effectiveChartConfig,
      dataSeriesConfig,
    );
    const withColors = applyDefaultColors(
      withOverrides,
      chartType,
      defaultPalette,
    );
    return { chartData: withColors, chartDataError: null };
  }, [
    transformResult,
    effectiveChartConfig,
    defaultPalette,
    chartType,
    dataSeriesConfig,
  ]);

  const { chartOptions, chartOptionsError } = useMemo(() => {
    if (transformResult.error || !transformResult.result) {
      return {
        chartOptions: { ...FALLBACK_CHART_OPTIONS },
        chartOptionsError: transformResult.error,
      };
    }

    const transformedOptions = transformResult.result.options ?? {};
    const userOptions = config.chartconfig?.options ?? {};
    return {
      chartOptions: {
        ...DEFAULT_CHART_OPTIONS,
        ...transformedOptions,
        ...userOptions,
        plugins: mergePlugins(
          DEFAULT_CHART_OPTIONS.plugins,
          transformedOptions.plugins,
          userOptions.plugins,
        ),
      } as ChartOptions,
      chartOptionsError: null,
    };
  }, [transformResult, config]);

  const error = chartDataError || chartOptionsError;

  return {
    chartConfig,
    chartType,
    chartTypeSupported,
    ChartComponent,
    chartData,
    chartOptions,
    error,
  };
};

export default useFlexibleChart;
